import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import config from 'cio/config/environment';

export default class GapiService extends Service {
  @tracked gapiLoaded = false;
  // Can't use namespace (gapi) as a type
  @tracked gapi: Record<string, any> | null = null;
  // Can't use namespace (google) as a type
  @tracked google: Record<string, any> | null = null;
  @tracked scriptElement: HTMLScriptElement | null = null;
  @tracked pickerLoaded = false;

  async openSpreadsheetPicker(accessToken: string, cb: (data: google.picker.ResponseObject) => unknown) {
    await this.loadGapi.perform();
    await this.loadPicker.perform();

    // Google picker reference docs: https://developers.google.com/picker/docs/reference
    let view = new this.google!.picker.DocsView(this.google!.picker.ViewId.SPREADSHEETS)
      .setMode(this.google!.picker.DocsViewMode.LIST)
      .setEnableDrives(false)
      .setSelectFolderEnabled(false)
      // Exclude Google Forms
      .setMimeTypes('application/vnd.google-apps.spreadsheet');

    let picker = new this.google!.picker.PickerBuilder()
      .enableFeature(this.google!.picker.Feature.SUPPORT_DRIVES)
      .enableFeature(this.google!.picker.Feature.NAV_HIDDEN)
      .setAppId(config.oAuth.googleSheets.clientId)
      .setOAuthToken(accessToken)
      .addView(view)
      .setCallback(cb)
      .build();

    picker.setVisible(true);

    return picker;
  }

  loadGapi = dropTask(async () => {
    if (this.gapiLoaded) {
      return;
    }

    await this.injectScript.perform();
    this.gapiLoaded = true;
    this.gapi = window.gapi;
  });

  loadPicker = dropTask(async () => {
    if (this.pickerLoaded) {
      return;
    }

    await new Promise((resolve, reject) => {
      this.gapi!.load('picker', { callback: resolve, onerror: reject });
    });

    this.pickerLoaded = true;
    this.google = window.google;
  });

  injectScript = dropTask(async () => {
    return new Promise((resolve, reject) => {
      let el = document.createElement('script');

      el.setAttribute('src', 'https://apis.google.com/js/api.js');
      el.setAttribute('async', 'true');
      el.addEventListener('load', resolve);
      el.addEventListener('error', reject);

      document.body.appendChild(el);
    });
  });
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    gapi: GapiService;
  }
}
